import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Clock, CheckCircle, Plus, Calendar, AlertCircle } from 'lucide-react';
import { Task, Organization } from '@/types/database';
interface TaskWidgetProps {
  userRole?: string;
}
const TaskWidget = ({
  userRole = 'standard'
}: TaskWidgetProps) => {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [completingTask, setCompletingTask] = useState<string | null>(null);
  useEffect(() => {
    loadTasks();
  }, []);
  const loadTasks = async () => {
    try {
      const {
        data: {
          user
        }
      } = await supabase.auth.getUser();
      if (!user) return;

      // Load user's organization
      const {
        data: profile
      } = await supabase.from('profiles').select('organization_id').eq('user_id', user.id).single();
      if (profile?.organization_id) {
        const {
          data: orgData
        } = await supabase.from('organizations').select('*').eq('id', profile.organization_id).single();
        setOrganization(orgData as Organization);
      }

      // Load tasks
      const {
        data: tasksData,
        error
      } = await supabase.from('tasks').select('*').eq('user_id', user.id).order('next_reminder', {
        ascending: true
      });
      if (error) throw error;
      setTasks(tasksData || []);
    } catch (error: any) {
      toast.error('Fehler beim Laden der Aufgaben: ' + error.message);
    } finally {
      setLoading(false);
    }
  };
  const completeTask = async (taskId: string) => {
    setCompletingTask(taskId);
    try {
      const task = tasks.find(t => t.id === taskId);
      if (!task) return;

      // Mark as completed and set next reminder
      const nextReminder = new Date();
      nextReminder.setDate(nextReminder.getDate() + task.reminder_interval_days);
      nextReminder.setHours(nextReminder.getHours() + task.reminder_interval_hours);
      const {
        error
      } = await supabase.from('tasks').update({
        completed: true,
        next_reminder: nextReminder.toISOString()
      }).eq('id', taskId);
      if (error) throw error;
      toast.success('Aufgabe abgeschlossen! 🎉');
      loadTasks();
    } catch (error: any) {
      toast.error('Fehler beim Abschließen der Aufgabe: ' + error.message);
    } finally {
      setCompletingTask(null);
    }
  };
  const resetTask = async (taskId: string) => {
    try {
      const {
        error
      } = await supabase.from('tasks').update({
        completed: false
      }).eq('id', taskId);
      if (error) throw error;
      toast.success('Aufgabe zurückgesetzt');
      loadTasks();
    } catch (error: any) {
      toast.error('Fehler beim Zurücksetzen: ' + error.message);
    }
  };
  const getDaysUntilReminder = (reminderDate: string) => {
    const now = new Date();
    const reminder = new Date(reminderDate);
    const diffTime = reminder.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };
  const getUrgencyColor = (daysUntil: number) => {
    if (daysUntil < 0) return 'text-red-500';
    if (daysUntil === 0) return 'text-orange-500';
    if (daysUntil <= 2) return 'text-yellow-500';
    return 'text-green-500';
  };
  const completedTasks = tasks.filter(t => t.completed);
  const pendingTasks = tasks.filter(t => !t.completed);
  const overdueTasks = pendingTasks.filter(t => t.next_reminder && getDaysUntilReminder(t.next_reminder) < 0);
  if (loading) {
    return <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Aufgaben-Planer
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-3">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded w-1/2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </div>
        </CardContent>
      </Card>;
  }
  return <div className="space-y-6">

      {/* Pending Tasks */}
      {pendingTasks.length > 0 && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Anstehende Aufgaben ({pendingTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {pendingTasks.map(task => {
            const daysUntil = task.next_reminder ? getDaysUntilReminder(task.next_reminder) : 0;
            return <Card key={task.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <div className="w-3 h-3 rounded-full" style={{
                      backgroundColor: task.color
                    }} />
                          <h4 className="font-medium">{task.title}</h4>
                        </div>
                        {task.description && <p className="text-sm text-muted-foreground mb-2">{task.description}</p>}
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span>Alle {task.reminder_interval_days}d {task.reminder_interval_hours}h</span>
                          {task.next_reminder && <span className={getUrgencyColor(daysUntil)}>
                              <Calendar className="inline h-3 w-3 mr-1" />
                              {daysUntil < 0 ? `${Math.abs(daysUntil)} Tage überfällig` : daysUntil === 0 ? 'Heute fällig' : `In ${daysUntil} Tagen`}
                            </span>}
                        </div>
                      </div>
                      <Button onClick={() => completeTask(task.id)} disabled={completingTask === task.id} size="sm" className="ml-4">
                        {completingTask === task.id ? 'Wird abgeschlossen...' : 'Erledigt'}
                      </Button>
                    </div>
                  </Card>;
          })}
            </div>
          </CardContent>
        </Card>}

      {/* Completed Tasks */}
      {completedTasks.length > 0 && <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-green-500" />
              Abgeschlossene Aufgaben ({completedTasks.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {completedTasks.slice(0, 5).map(task => <Card key={task.id} className="p-3 bg-green-50 border-green-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="font-medium text-green-700">{task.title}</span>
                    </div>
                    {(userRole === 'admin' || userRole === 'teamleader') && <Button onClick={() => resetTask(task.id)} variant="ghost" size="sm" className="text-xs">
                        Zurücksetzen
                      </Button>}
                  </div>
                </Card>)}
              {completedTasks.length > 5 && <p className="text-sm text-muted-foreground text-center">
                  ... und {completedTasks.length - 5} weitere
                </p>}
            </div>
          </CardContent>
        </Card>}

      {/* Empty State */}
      {tasks.length === 0}
    </div>;
};
export default TaskWidget;